import React, { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { SERVER_IP } from 'assets/Config';
import { ACTIONS, API_STATUS, DATE_FORMAT, SUPER_ADMIN_EMAILS } from 'constants/app-constants';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { formQueryStringFromObject } from 'helpers';
import {
	fetchDashboardCustomerBirthdays,
	fetchDashboardCustomerReferrers,
	fetchDashboardCustomerTrends,
	fetchDashboardCustomersSummary,
} from 'services/dashboardCustomers';
import DashboardPresentational from './dashboard-presentational';

const DashboardFunctional = () => {
	const loginRedux = useSelector((state) => state.loginRedux);
	const globalRedux = useSelector((state) => state.globalRedux);
	const dashboardData = useSelector(({ dashboardRedux }) => dashboardRedux?.dashboard);
	const payments = useSelector((state) => state.paymentRedux?.payments);
	const branches = useSelector(({ globalRedux: { branches } }) => branches);
	const branchId = globalRedux?.selectedBranch?.id;
	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loginRedux.email);
	const selectedBranchId = isSuperAdmin ? undefined : branchId;
	const [, setCustomerSummary] = React.useState(null);
	const [, setCustomerTrends] = React.useState([]);
	const [customerBirthdays, setCustomerBirthdays] = React.useState([]);
	const [customerReferrers, setCustomerReferrers] = React.useState([]);
	const [extrasLoading, setExtrasLoading] = React.useState(false);

	const dispatch = useDispatch();
	const loading = React.useMemo(
		() =>
			globalRedux.apiStatus?.[ACTIONS.GET_DASHBOARD_CUSTOMERS] === API_STATUS.PENDING ||
			globalRedux.apiStatus?.[ACTIONS.GET_DASHBOARD_PAYMENTS] === API_STATUS.PENDING ||
			extrasLoading,
		[extrasLoading, globalRedux.apiStatus]
	);

	const getProductsSales = useCallback(
		(month) => {
			if (isSuperAdmin) {
				return;
			}
			const string = formQueryStringFromObject({
				branchId: selectedBranchId,
				reportFor: 'product',
				months: 6,
				month: dayjs(month).format(DATE_FORMAT.MM_YYYY),
				isSuperAdmin,
			});
			const URL = `${SERVER_IP}api/dashboard/getMonthSales?${string}`;
			dispatch(getApi('GET_PRODUCTS_SALE', URL));
		},
		[dispatch, isSuperAdmin, selectedBranchId]
	);

	const getMembershipSales = useCallback(
		(month) => {
			const string = formQueryStringFromObject({
				branchId: selectedBranchId,
				reportFor: 'membership',
				months: 6,
				month: dayjs(month).format(DATE_FORMAT.MM_YYYY),
				isSuperAdmin,
			});
			const URL = `${SERVER_IP}api/dashboard/getMonthSales?${string}`;
			dispatch(getApi('GET_MEMBERSHIP_SALE', URL));
		},
		[dispatch, isSuperAdmin, selectedBranchId]
	);

	const buildDashboardParams = useCallback(
		(month) => {
			const selectedMonth = month ? dayjs(month) : dayjs();
			const monthFilter = selectedMonth.format(DATE_FORMAT.MM_YYYY);
			return {
				branchId: selectedBranchId,
				month: monthFilter,
				fromDate: selectedMonth.startOf('month').toISOString(),
				toDate: selectedMonth.endOf('month').toISOString(),
				isSuperAdmin,
			};
		},
		[isSuperAdmin, selectedBranchId]
	);

	const buildDashboardQueryString = useCallback((month) => formQueryStringFromObject(buildDashboardParams(month)), [buildDashboardParams]);

	const showExtrasError = useCallback((label, error) => {
		if (!error) return;
		const errorMessage = error?.message || `Unable to load ${label}`;
		message.error(errorMessage);
	}, []);

	const fetchDashboardExtras = useCallback(
		async (rMonth) => {
			if (isSuperAdmin || !branchId) return;
			setExtrasLoading(true);
			const params = buildDashboardParams(rMonth);
			try {
				const [birthdaysRes, referrersRes, summaryRes, trendsRes] = await Promise.all([
					fetchDashboardCustomerBirthdays(params),
					fetchDashboardCustomerReferrers(params),
					fetchDashboardCustomersSummary(params),
					fetchDashboardCustomerTrends(params),
				]);

				if (birthdaysRes?.data) {
					setCustomerBirthdays(birthdaysRes.data?.data ?? birthdaysRes.data ?? []);
				} else {
					setCustomerBirthdays([]);
					showExtrasError('upcoming birthdays', birthdaysRes?.error);
				}

				if (referrersRes?.data) {
					setCustomerReferrers(referrersRes.data?.data ?? referrersRes.data ?? []);
				} else {
					setCustomerReferrers([]);
					showExtrasError('top referrers', referrersRes?.error);
				}

				if (summaryRes?.data) {
					setCustomerSummary(summaryRes.data);
				} else {
					setCustomerSummary(null);
					showExtrasError('customer summary', summaryRes?.error);
				}

				if (trendsRes?.data) {
					setCustomerTrends(trendsRes.data);
				} else {
					setCustomerTrends([]);
					showExtrasError('customer trends', trendsRes?.error);
				}
			} finally {
				setExtrasLoading(false);
			}
		},
		[branchId, buildDashboardParams, isSuperAdmin, showExtrasError]
	);

	const getDashboardData = useCallback(
		(rMonth) => {
			if (!isSuperAdmin && !branchId) {
				return;
			}
			const string = buildDashboardQueryString(rMonth);
			const customersUrl = `${SERVER_IP}api/dashboard?${string}`;
			const paymentsUrl = `${SERVER_IP}api/dashboard/payments?${string}`;
			dispatch(getApi(ACTIONS.GET_DASHBOARD_CUSTOMERS, customersUrl));
			dispatch(getApi(ACTIONS.GET_DASHBOARD_PAYMENTS, paymentsUrl));
			getMembershipSales(rMonth);
			if (!isSuperAdmin) {
				getProductsSales(rMonth);
				fetchDashboardExtras(rMonth);
			}
		},
		[branchId, buildDashboardQueryString, dispatch, fetchDashboardExtras, getMembershipSales, getProductsSales, isSuperAdmin]
	);

	const getPayments = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId: selectedBranchId,
			type: 'all',
			isSuperAdmin,
		});
		const url = `${SERVER_IP}api/payments?limit=10&${string}`;
		dispatch(getApi('GET_PAYMENTS', url));
	}, [dispatch, isSuperAdmin, selectedBranchId]);

	// Extract birthdays and referrers from dashboard data
	const upcomingBirthdays = React.useMemo(() => {
		if (!Array.isArray(customerBirthdays)) return [];
		return customerBirthdays;
	}, [customerBirthdays]);

	const topReferrers = React.useMemo(() => {
		if (!Array.isArray(customerReferrers)) return [];
		return customerReferrers.map((item) => ({
			id: item.referrer?.id,
			customerName: item.referrer?.customerName || item.referrer?.displayName || 'Unknown',
			displayName: item.referrer?.displayName,
			referralCount: item.referralsCount || 0,
		}));
	}, [customerReferrers]);

	useEffect(() => {
		if (selectedBranchId || isSuperAdmin) {
			getDashboardData();
			getPayments();
		}
		// getProductsSales(dayjs());
		// getMembershipSales(dayjs());
	}, [getDashboardData, getMembershipSales, getPayments, getProductsSales, isSuperAdmin, selectedBranchId]);

	return (
		<DashboardPresentational {...{ dashboardData, getDashboardData, loginRedux, branches, payments, loading, upcomingBirthdays, topReferrers }} />
	);
};

export default DashboardFunctional;
